import { getStorageKey } from "common/constants";
import { createCookieStorage } from "stores/cookieStorage";
import { User, UserRole } from "types/interface.types";
import { BackOrders } from "types/model.types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type SessionState = {
  user: User | null;
  backOrders: BackOrders;
  selectedTheme: "Light" | "Dark";
  selectedSystem: "Default" | "Admin";

  isStaff: boolean;
  isAdmin: boolean;
  isLoggedIn: boolean;
  priceLevel: number;
  isGlobalAdmin: boolean;
  isCustomer: boolean;

  hydrated: boolean;
  sessionVerified: boolean;

  setHydrated: () => void;
  setSessionVerified: () => void;
  setUser: (user: User) => void;
  clearUser: () => void;
  setBackOrders: (orders: BackOrders) => void;
  setTheme: (theme: "Light" | "Dark") => void;
  setSystem: (sys: "Default" | "Admin") => void;
};

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      user: null,
      backOrders: [],
      selectedTheme: "Light",
      selectedSystem: "Default",
      priceLevel: 1,
      isStaff: false,
      isAdmin: false,
      isLoggedIn: false,
      isGlobalAdmin: false,
      isCustomer: false,

      hydrated: false,
      sessionVerified: false,

      setHydrated: () => set({ hydrated: true }),
      setSessionVerified: () => set({ sessionVerified: true }),

      setUser: (user) => {
        const role = user?.role ?? UserRole.Anonymous;
        set({
          user,
          isLoggedIn: true,
          priceLevel: user?.priceLevel ?? 1,
          isStaff: [
            UserRole.Staff,
            UserRole.Administrator,
            UserRole.SuperUser,
          ].includes(role),
          isAdmin: [UserRole.Administrator, UserRole.SuperUser].includes(role),
          isGlobalAdmin: role === UserRole.SuperUser,
          isCustomer: role === UserRole.Customer,
          sessionVerified: true,
        });
      },

      clearUser: () => {
        set({
          user: null,
          isAdmin: false,
          isLoggedIn: false,
          isGlobalAdmin: false,
          isCustomer: false,
        });
      },

      setBackOrders: (orders) => set({ backOrders: orders }),
      setTheme: (theme) => set({ selectedTheme: theme }),
      setSystem: (sys) => {
        set({ selectedSystem: sys });
      },
    }),
    {
      name: `${getStorageKey("useSessionStore")}`,
      storage: createJSONStorage(() =>
        createCookieStorage({
          path: `/`,
          domain: `${window.location.hostname}`,
          sameSite: "None",
          secure: true,
        }),
      ),
      onRehydrateStorage: () => (state, error) => {
        state?.setHydrated?.();
      },
    },
  ),
);
